import { CLINIC } from "@/lib/api";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, CalendarClock } from "lucide-react";

export function CTASection({ title = "Primul pas este consultația.", subtitle = "Echipa clinicii vă va contacta pentru confirmarea solicitării." }) {
  return (
    <section className="bg-[#0F2C59] text-white rounded-3xl px-6 md:px-14 py-14 md:py-20 my-16 md:my-24" data-testid="cta-section">
      <div className="max-w-4xl">
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl">{title}</h2>
        <p className="text-white/70 mt-4 text-base md:text-lg">
          {subtitle}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <a href={CLINIC.phoneTel} className="inline-flex items-center justify-center gap-2 bg-white text-[#0F2C59] rounded-full px-6 py-4 font-semibold hover:bg-slate-100 transition-colors" data-testid="cta-call-btn">
            <Phone className="w-4 h-4" /> Sună acum
          </a>
          <a href={CLINIC.whatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-full px-6 py-4 font-semibold hover:bg-[#128C7E] transition-colors" data-testid="cta-whatsapp-btn">
            <MessageCircle className="w-4 h-4" /> Scrie pe WhatsApp
          </a>
          <Link to="/contact#programare" className="inline-flex items-center justify-center gap-2 bg-[#B4262A] text-white rounded-full px-6 py-4 font-semibold hover:bg-[#8E1D20] transition-colors" data-testid="cta-appointment-btn">
            <CalendarClock className="w-4 h-4" /> Solicită o programare
          </Link>
        </div>
      </div>
    </section>
  );
}

export function TrustBadges() {
  const items = [
    "Existăm din anul 1992",
    "Investigații în clinică",
    "Tehnologie digitală",
    "Planuri personalizate",
    "Colaborare medicală în cazurile complexe",
  ];
  return (
    <div className="flex flex-wrap gap-2" data-testid="trust-badges">
      {items.map((t) => (
        <span key={t} className="inline-flex items-center px-4 py-2 rounded-full bg-slate-100 text-slate-800 text-xs sm:text-sm font-semibold border border-slate-200">
          {t}
        </span>
      ))}
    </div>
  );
}

export function Disclaimer({ children }) {
  return (
    <div className="border-l-4 border-[#D97757] bg-orange-50/60 px-5 py-4 rounded-r-xl text-sm text-slate-700 my-6" data-testid="disclaimer-box">
      {children}
    </div>
  );
}

export function PageHero({ eyebrow, title, lead, image, titleAs = "h1" }) {
  // titleAs allows a page to downgrade the hero heading (e.g. from h1 to h2)
  // when the actual document H1 lives inside embedded content — used by the
  // Soro-powered /blog page to avoid two competing H1s on article-detail
  // views (where the article title itself becomes the document's single H1).
  const Heading = titleAs;
  return (
    <section className="max-w-7xl mx-auto px-5 md:px-8 pt-12 md:pt-20 pb-10 md:pb-16 grid gap-6 md:gap-10 md:grid-cols-2 items-center">
      <div>
        {eyebrow && <div className="text-[#D97757] text-sm font-semibold uppercase tracking-widest mb-4">{eyebrow}</div>}
        <Heading className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#0F2C59]">{title}</Heading>
        {lead && <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed">{lead}</p>}
      </div>
      {/*
        Mobile-first: cap the hero illustration to a compact banner (~140 px tall
        on 390 px viewports) so the H1, lead and CTA remain the primary focus.
        On tablet+ the full 4:3 composition is restored. The `[&>div]:!aspect-*`
        selector overrides the aspect class inside ImagePlaceholder / BrandedVisual
        without touching any call site.
      */}
      <div className="[&>div]:!aspect-[16/6] md:[&>div]:!aspect-[4/3] order-first md:order-none">{image}</div>
    </section>
  );
}
